import React, { useEffect, useState } from "react";
import "./Style.css";
import "./PrivacyPolicy.css";
import Footer from "./Footer";

const LAST_UPDATED = "October 2026";

const GRIEVANCE_OFFICER = {
  name: "Rao P",
  email: "grievance@vbrsit.in",
  address: "VBRS IT Solutions, Sandhya Techno-1, 10th Floor, C9CM+MRR, X Road, Radhe Nagar, Khajaguda, Raidurg, Telangana – 500032",
};

const sections = [
  {
    id: "introduction",
    title: "Introduction",
    body: (
      <>
        <p>
          CUB HRM is a cloud-based human resource management, payroll, invoicing and statutory compliance platform
          owned and operated by <strong>VBRS IT Solutions</strong> ("VBRS", "we", "us" or "our").
        </p>
        <p>
          This Privacy Policy describes how we collect, use, disclose, store and protect personal data in
          connection with our website, the CUB HRM web application and the CUB HRM mobile application
          (collectively, the "Platform"). It also explains the rights available to individuals whose personal data
          is processed through the Platform. By accessing or using the Platform, you acknowledge that you have read
          and understood this Privacy Policy.
        </p>
      </>
    ),
  },
  {
    id: "scope-and-role",
    title: "Scope and Our Role",
    body: (
      <>
        <p>
          Organisations that subscribe to the Platform ("Customers") use it to manage information relating to their
          employees, candidates, clients and authorised users. In respect of such information, the Customer
          determines the purpose and means of processing and acts as the <strong>Data Fiduciary</strong>. VBRS
          processes this information on behalf of, and in accordance with the instructions of, the Customer, and
          acts as a <strong>Data Processor</strong>.
        </p>
        <p>
          VBRS acts as a Data Fiduciary only for personal data it collects for its own purposes, such as
          information submitted through our website, demo requests and account administration.
        </p>
        <p>
          If you are an employee or candidate of a Customer, requests relating to your personal data should be
          directed to that Customer in the first instance. We will assist the Customer in responding to such
          requests as required.
        </p>
      </>
    ),
  },
  {
    id: "data-we-collect",
    title: "Personal Data We Collect",
    body: (
      <>
        <p>Depending on how the Platform is used, we may process the following categories of personal data:</p>
        <ul>
          <li><strong>Organisation details</strong> such as company name, registration details and contact information</li>
          <li><strong>Identity and contact details</strong> such as name, email address and phone number</li>
          <li><strong>Employment and payroll details</strong> such as designation, salary, payslips, bank and statutory details</li>
          <li><strong>Attendance and leave records</strong>, including location at the time of check-in and check-out</li>
          <li><strong>Documents</strong> uploaded to the Platform by Customers, employees or candidates</li>
          <li><strong>Account information</strong> such as login credentials and one-time passwords</li>
          <li><strong>Website enquiries</strong> submitted through our registration and demo request forms</li>
        </ul>
      </>
    ),
  },
  {
    id: "purposes",
    title: "Purposes of Processing",
    body: (
      <>
        <p>We process personal data only for specified and lawful purposes, including to:</p>
        <ul>
          <li>Provide, operate and maintain the Platform and its features</li>
          <li>Administer employee records, attendance, leave, payroll, payslips and official letters</li>
          <li>Generate invoices and support statutory compliance, including GST, Provident Fund, Professional Tax and TDS</li>
          <li>Authenticate users, secure accounts and prevent unauthorised access</li>
          <li>Send service communications, including one-time passwords, notifications and payroll updates</li>
          <li>Respond to demo requests, support queries and feedback</li>
          <li>Comply with applicable laws, regulations and lawful requests from authorities</li>
        </ul>
        <p>We do not use personal data processed on behalf of Customers for advertising or marketing purposes.</p>
      </>
    ),
  },
  {
    id: "location-information",
    title: "Location Information",
    body: (
      <p>
        When an employee marks attendance through the web application or the mobile application, the Platform
        records the employee's location at the time of check-in and check-out for the purpose of verifying
        attendance. Location information is accessible to the employee and to authorised users of the
        Customer within attendance records. Map links within the Platform are opened using Google Maps, which is
        governed by Google's own privacy policy.
      </p>
    ),
  },
  {
    id: "disclosure",
    title: "Disclosure of Personal Data",
    body: (
      <>
        <p>
          <strong>We do not sell, rent or trade personal data.</strong> We disclose personal data only in the
          following circumstances:
        </p>
        <ul>
          <li>To the Customer to whom the personal data relates, and to users authorised by that Customer</li>
          <li>
            To trusted service providers who support the operation of the Platform, such as infrastructure hosting
            and email delivery providers, who are permitted to process personal data only as necessary to provide
            their services to us
          </li>
          <li>Where required by applicable law, regulation, court order or a lawful request from a government authority</li>
          <li>Where necessary to protect the rights, property or safety of VBRS, our Customers or others</li>
        </ul>
      </>
    ),
  },
  {
    id: "data-security",
    title: "Data Security",
    body: (
      <>
        <p>
          We implement reasonable security practices and procedures, including secure data transmission, access
          controls and user authentication, to protect personal data against unauthorised access, alteration,
          disclosure or destruction.
        </p>
        <p>
          While we take these measures seriously, no method of electronic transmission or storage is completely
          secure, and we cannot guarantee absolute security.
        </p>
      </>
    ),
  },
  {
    id: "storage-and-retention",
    title: "Data Storage and Retention",
    body: (
      <>
        <p>
          Personal data processed through the Platform is stored on servers located in <strong>India</strong>.
        </p>
        <p>
          We retain personal data for as long as the Customer's account remains active, or as long as necessary to
          fulfil the purposes described in this Privacy Policy. Upon termination of a Customer's account and receipt
          of a deletion request, we will delete the Customer's data within <strong>30 days</strong>, except where
          retention is required for a longer period under applicable law, such as payroll, tax and statutory
          records.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your Rights",
    body: (
      <>
        <p>
          Subject to applicable law, including the Digital Personal Data Protection Act, 2023, you have the right
          to:
        </p>
        <ul>
          <li>Obtain a summary of the personal data processed about you</li>
          <li>Request correction, completion or updating of inaccurate or incomplete personal data</li>
          <li>Request erasure of personal data that is no longer required, subject to legal retention obligations</li>
          <li>Withdraw consent, where processing is based on consent</li>
          <li>Seek redressal of grievances relating to the processing of your personal data</li>
          <li>Nominate another individual to exercise these rights on your behalf in the event of death or incapacity</li>
        </ul>
        <p>
          Where your personal data has been provided by a Customer, such as your employer, please submit your
          request to that Customer. Other requests may be sent to our Grievance Officer using the details below.
        </p>
      </>
    ),
  },
  {
    id: "grievance-redressal",
    title: "Grievance Redressal",
    body: (
      <>
        <p>
          In accordance with applicable law, we have appointed a Grievance Officer to address questions, requests
          and complaints relating to the processing of personal data. You may contact the Grievance Officer at:
        </p>
        <div className="pv-contact">
          <p><strong>{GRIEVANCE_OFFICER.name}</strong>, Grievance Officer</p>
          <p>
            Email: <a href={`mailto:${GRIEVANCE_OFFICER.email}`}>{GRIEVANCE_OFFICER.email}</a>
          </p>
          <p>Address: {GRIEVANCE_OFFICER.address}</p>
        </div>
        <p>We will acknowledge your grievance promptly and endeavour to resolve it within 30 days of receipt.</p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our practices, the Platform or
        applicable law. The updated version will be published on this page with a revised "Last updated" date. We
        encourage you to review this Privacy Policy periodically. Continued use of the Platform after any update
        constitutes acknowledgement of the revised Privacy Policy.
      </p>
    ),
  },
];

const PrivacyPolicy = () => {
  const [activeId, setActiveId] = useState(sections[0].id);

  // Highlights the sidebar link of the section currently being read
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -65% 0px" }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (e, id) => {
    e.preventDefault();
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="pricing-page-root">
      <header className="pricing-page-header">
        <div className="pv-header-inner">
          <a href="/" className="pricing-page-logo">
            <img src="assets/img/website/newDesCubLogo.png" alt="CUB HRM" className="pricing-logo-img" />
          </a>
          <nav className="pricing-page-nav-links">
            <a href="/">Home</a>
            <a href="/pricing">Pricing</a>
          </nav>
        </div>
      </header>

      <section className="pv-hero">
        <div className="pv-hero-inner">
          <span className="pv-eyebrow">Legal</span>
          <h1 className="pricing-page-title">Privacy Policy</h1>
          <p className="pricing-page-subtitle">How CUB HRM collects, uses and protects personal data.</p>
          <span className="pv-updated">Last updated: {LAST_UPDATED}</span>
        </div>
      </section>

      <div className="pv-layout">
        <aside className="pv-sidebar">
          <nav aria-label="On this page">
            <p className="pv-toc-title">On this page</p>
            <ol className="pv-toc-list">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className={activeId === s.id ? "active" : ""}
                    onClick={(e) => scrollToSection(e, s.id)}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </aside>

        <main className="pv-content">
          <p className="pv-intro">
            At VBRS IT Solutions, we are committed to protecting the privacy and security of the personal data
            entrusted to us. This Privacy Policy sets out our practices in a clear and transparent manner.
          </p>

          {sections.map((s) => (
            <section key={s.id} id={s.id} className="pv-section">
              <h2>{s.title}</h2>
              {s.body}
            </section>
          ))}
        </main>
      </div>

      <Footer id="pv-footer" />
    </div>
  );
};

export default PrivacyPolicy;
